import { useCallback, useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Camera, MapPin, Phone, Car, Truck, Route, Tag, FileText, Calendar, AlertTriangle, Moon, Upload, Mail, Loader2, ExternalLink, Receipt, Warehouse, X, Lock, Printer, Undo2, Link2, Download } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { fetchJobPhotos, uploadJobPhoto, deleteJobPhoto, fetchJobPartnerLink, markJobPartnerOpened, setManualTowCharge, setStorageCharge, setBillTo, knownPayers, fetchBusinessInfo, unlinkYard, recalculateJobKm, confirmJobKm, downloadJobPhotoBundle, api } from "../lib/api";
import JobTimeline from "./JobTimeline";
import { printHtmlViaIframe } from "../lib/printHtml";
import EmailInsurerDialog from "./EmailInsurerDialog";
import EmailProformaDialog from "./EmailProformaDialog";
import { useAuth } from "../lib/auth";
import { SERVICE_LABEL, STATUS_LABEL, STATUS_COLORS } from "../lib/constants";
import { cn, formatDistance } from "../lib/utils";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { tenantWeekday } from "../lib/tenantTime";
import JobHistoryPanel from "./JobHistoryPanel";
import { hasCap, can } from "../lib/roles";

const SKIP_REASON_LABEL = {
  emergency_accident: { icon: AlertTriangle, label: "Emergency / Accident", cls: "text-rose-700 bg-rose-50 dark:bg-rose-950/30 dark:text-rose-200" },
  nighttime_too_dark: { icon: Moon, label: "Nighttime / Too Dark", cls: "text-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 dark:text-indigo-200" },
  no_damage_to_record: { icon: Camera, label: "No damage to record", cls: "text-slate-600 bg-slate-50 dark:bg-slate-800/40 dark:text-slate-300" },
};

/**
 * Full read-only job detail panel for History. Shows every field on one
 * screen plus the photo gallery (tap to enlarge). Used when someone clicks
 * a row in HistoryPage so dispatchers / billing can quickly inspect a job
 * for invoicing or a damage-claim follow-up.
 */
export default function JobDetailSheet({ job, open, onOpenChange, truckById }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState(job?.photos || []);
  const [zoomed, setZoomed] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);
  const [proformaDialogOpen, setProformaDialogOpen] = useState(false);
  const [proformaCtx, setProformaCtx] = useState(null); // {lineItems, subtotal, taxRate, taxLabel}
  const [aaQuote, setAaQuote] = useState(null);
  const [storageQuote, setStorageQuote] = useState(null);
  const [savingYard, setSavingYard] = useState(false);
  // Yard storage charge, when the office charges something other than
  // days × rate. Kept as a string so "" (charge the clock) is distinct
  // from "0" (charging nothing).
  const [storageChargeInput, setStorageChargeInput] = useState("");
  const [storageChargeReason, setStorageChargeReason] = useState("");
  const [savingStorageCharge, setSavingStorageCharge] = useState(false);
  const [billToInput, setBillToInput] = useState("");
  const [savingBillTo, setSavingBillTo] = useState(false);
  const [payers, setPayers] = useState([]);
  // Manual tow charge — edited inline in the Invoice Summary. Kept in
  // local string state (not a number) so we can distinguish "" (unset)
  // from "0" (explicit zero). Saved on blur / Enter with a small debounce.
  const [manualTowInput, setManualTowInput] = useState("");
  const [savingManualTow, setSavingManualTow] = useState(false);
  // Recalculate-km button state — spinner + optimistic patch on success.
  const [recomputingKm, setRecomputingKm] = useState(false);
  const [confirmingKm, setConfirmingKm] = useState(false);
  // Linked outbound "on-tow out" job — priced separately so the billing user can
  // invoice tow-in + storage + tow-out from this one panel.
  const [outboundJob, setOutboundJob] = useState(null);
  // GST rate for the Invoice Summary's ex/incl toggle. Same source the
  // proforma and the backend PDF use (business_info.tax_rate), so the three
  // can never disagree on what GST is for this tenant.
  const [gstCfg, setGstCfg] = useState({ rate: 0.15, label: "GST" });
  const [showInclGst, setShowInclGst] = useState(false);
  // Every amount in the database is NET of GST. These two convert between
  // that and whatever the ex/incl button is currently showing. `toGstView`
  // is what the office sees; `fromGstView` is how a figure they type is
  // read back to a net amount.
  const toGstView = useCallback(
    (net) => (net == null || net === "" ? net
      : showInclGst ? +(Number(net) * (1 + gstCfg.rate)).toFixed(2) : Number(net)),
    [showInclGst, gstCfg.rate],
  );
  const fromGstView = useCallback(
    (shown) => (shown == null ? null
      : showInclGst ? +(Number(shown) / (1 + gstCfg.rate)).toFixed(2) : Number(shown)),
    [showInclGst, gstCfg.rate],
  );
  const [towOutInput, setTowOutInput] = useState("");
  const [savingTowOut, setSavingTowOut] = useState(false);
  const fileInputRef = useRef(null);

  // Display helper: format a stored number as always-2dp so a tow charge of
  // 250 renders "250.00" not "250" (Aug 2026 — matches invoicing convention).
  // Hoisted above the useEffects so both the outbound-fetch effect and the
  // manual-tow sync effect can reference it.
  const fmt2dp = (raw) => {
    if (raw == null || raw === "") return "";
    const n = Number(raw);
    return Number.isFinite(n) ? n.toFixed(2) : String(raw);
  };
  // Feb 2026: dispatcher can VIEW archived jobs (via the History "Archived"
  // filter option) but must NOT be able to mutate them. Admin + viewer keep
  // full manage rights on archived rows.
  const dispatcherReadOnly = user?.role === "dispatcher" && !!job?.archived_at;
  // Writes to this archived job are gated (Attach, delete photo, edit manual
  // tow, etc.) — those stay on `canAttach`. But Download / Email Insurer are
  // pure READS over photos that already exist; a dispatcher chasing an
  // insurer dispute on an archived job (e.g. HSZ191 incident Oct 2026)
  // must still be able to grab the ZIP. Hence a separate role-only gate.
  const canAttach = (hasCap(user, "admin", "dispatcher")) && !dispatcherReadOnly;
  const canReadPhotoActions = hasCap(user, "admin", "dispatcher");
  const canEditManualTow = hasCap(user, "admin", "dispatcher") && !dispatcherReadOnly
    && can(user, "edit_prices");
  // Deciding who gets the invoice is a billing decision, same switch that
  // sets the billing state.
  const canSetBillTo = hasCap(user, "admin", "dispatcher") && !dispatcherReadOnly
    && can(user, "set_billing_state");

  // Append the auth JWT as ?t=... so <img> tags can load protected photos —
  // image elements can't send the Authorization header that axios uses.
  const authToken = typeof window !== "undefined" ? localStorage.getItem("rescue_token") : null;
  const photoSrc = (p) =>
    `${process.env.REACT_APP_BACKEND_URL}${p.url}${authToken ? `?t=${authToken}` : ""}`;

  // Re-fetch photos on open (so newly attached photos appear without a full refresh).
  useEffect(() => {
    if (!open || !job?.id) return;
    setPhotos(job.photos || []); // reset to current job's photos to avoid showing previous job's pics while fetching
    fetchJobPhotos(job.id).then(setPhotos).catch(() => {});
  }, [open, job?.id, job?.photos]);

  // Fetch the fee breakdown for jobs paid by external providers. Both AA
  // Workforms and ServiceNow First Rescue invoice the operator on the same
  // rate card (same trucks, same NZ rules) — so we reuse `/jobs/{id}/aa-quote`
  // for both sources. Gives billing + dispatcher a visible reconciliation of
  // what the upstream invoice should total.
  //
  // Include all four upstream ingestion channels: AA workforms, AA SMS
  // (fallback ingest path), ServiceNow API, and ServiceNow image capture.
  // Missing `aa_sms` and `servicenow_image` here previously meant those
  // jobs opened with no fee breakdown — the billing user had nothing to invoice off.
  const hasExternalProvider =
    job?.source === "aa_workforms" ||
    job?.source === "aa_sms" ||
    job?.source === "servicenow_api" ||
    job?.source === "servicenow_image";
  const providerLabel =
    (job?.source === "servicenow_api" || job?.source === "servicenow_image")
      ? "First Rescue"
      : "AA";
  useEffect(() => {
    if (!open || !job?.id || !hasExternalProvider) {
      setAaQuote(null);
      return;
    }
    setAaQuote(null);
    api.get(`/jobs/${job.id}/aa-quote`)
      .then((r) => setAaQuote(r.data))
      .catch(() => setAaQuote(null));
  }, [open, job?.id, hasExternalProvider]);

  // The tenant's GST rate, for the Invoice Summary toggle. Fetched once per
  // open rather than per click so flipping ex/incl is instant.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchBusinessInfo()
      .then((biz) => {
        if (cancelled) return;
        setGstCfg({
          rate: Number(biz?.tax_rate ?? 0.15),
          label: biz?.tax_label || "GST",
        });
      })
      .catch(() => { /* fall back to the 0.15 / "GST" default */ });
    return () => { cancelled = true; };
  }, [open]);

  // Fetch tow-job storage fee. Skipped for impound jobs (their own pricing
  // engine handles storage / overstay).
  useEffect(() => {
    if (!open || !job?.id || job?.source === "impound" || !job?.drop_off_address) {
      setStorageQuote(null);
      return;
    }
    setStorageQuote(null);
    api.get(`/jobs/${job.id}/storage-quote`)
      .then((r) => setStorageQuote(r.data))
      .catch(() => setStorageQuote(null));
  }, [open, job?.id, job?.source, job?.drop_off_address]);

  // Fetch the linked outbound tow-out job (created via On-tow out) so its
  // charge shows on this parent's invoice summary.
  useEffect(() => {
    if (!open || !job?.outbound_job_id) {
      setOutboundJob(null);
      setTowOutInput("");
      return;
    }
    api.get(`/jobs/${job.outbound_job_id}`)
      .then((r) => setOutboundJob(r.data))
      .catch(() => setOutboundJob(null));
  }, [open, job?.outbound_job_id]);

  // Keep the tow-out box showing the same money as the ex/incl button says,
  // without refetching the outbound job every time it's flipped.
  useEffect(() => {
    setTowOutInput(fmt2dp(toGstView(outboundJob?.manual_tow_charge)));
  }, [toGstView, outboundJob?.manual_tow_charge]);

  // Yard-outbound auto-link cross-references (Feb 2026): for jobs that
  // arrived from AA/FR/email/manual-retro and were auto-linked to a
  // yarded parent — surface both directions on the detail sheet.
  const [linkedYardParent, setLinkedYardParent] = useState(null);
  const [linkedReleaseChild, setLinkedReleaseChild] = useState(null);
  useEffect(() => {
    if (!open) { setLinkedYardParent(null); return; }
    if (!job?.linked_yard_job_id) { setLinkedYardParent(null); return; }
    api.get(`/jobs/${job.linked_yard_job_id}`)
      .then((r) => setLinkedYardParent(r.data))
      .catch(() => setLinkedYardParent(null));
  }, [open, job?.linked_yard_job_id]);
  useEffect(() => {
    if (!open) { setLinkedReleaseChild(null); return; }
    if (!job?.released_via_job_id) { setLinkedReleaseChild(null); return; }
    api.get(`/jobs/${job.released_via_job_id}`)
      .then((r) => setLinkedReleaseChild(r.data))
      .catch(() => setLinkedReleaseChild(null));
  }, [open, job?.released_via_job_id]);

  const handleChangeYard = async (slug) => {
    if (!job?.id || slug === storageQuote?.yard_slug) return;
    setSavingYard(true);
    try {
      const updated = await api.put(`/jobs/${job.id}/storage-yard`, { storage_yard: slug });
      setStorageQuote(updated.data);
      toast.success("Storage yard updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set yard");
    } finally {
      setSavingYard(false);
    }
  };

  // Sync the manual tow input from the job doc every time a new job opens
  // (or the doc gets refreshed after a save). Renders as an empty string
  // when unset so the placeholder shows. Always 2dp when populated.
  //
  // Jun 2026 — also re-runs when the ex/incl-GST button is flipped. Stored
  // amounts are ALWAYS net of GST; the button only decides how that money is
  // presented and how a figure typed into this box is read. Flipping must
  // never change what the customer is charged, so the figure on screen is
  // re-derived from the saved net rather than left as typed.
  useEffect(() => {
    if (!open) return;
    setManualTowInput(fmt2dp(toGstView(job?.manual_tow_charge)));
  }, [open, job?.id, job?.manual_tow_charge, toGstView]);

  // Mirror the saved storage charge into the box (blank when the clock's
  // figure is being charged as-is).
  useEffect(() => {
    setStorageChargeInput(storageQuote?.charge_adjusted
      ? Number(storageQuote.total).toFixed(2) : "");
    setStorageChargeReason(storageQuote?.adjustment_reason || "");
  }, [storageQuote?.charge_adjusted, storageQuote?.total, storageQuote?.adjustment_reason]);

  useEffect(() => {
    if (!open) return;
    setBillToInput(job?.bill_to_name || "");
    if (!canSetBillTo) return;
    knownPayers().then(setPayers).catch(() => setPayers([]));
  }, [open, job?.id, job?.bill_to_name, canSetBillTo]);

  const commitManualTowCharge = async () => {
    if (!job?.id || !canEditManualTow || savingManualTow) return;
    const trimmed = manualTowInput.trim();
    const typed = trimmed === "" ? null : Number(trimmed);
    if (typed != null && (!Number.isFinite(typed) || typed < 0)) {
      toast.error("Enter a valid amount (or leave blank to clear).");
      return;
    }
    // The lit button says what the figure MEANS: with incl-GST lit, a typed
    // 115 is a $100 charge plus GST. The database only ever holds the net.
    const parsed = typed == null ? null : fromGstView(typed);
    // Skip the round-trip if nothing changed — avoids spurious writes on
    // blur when the user just tabbed through.
    const current = job.manual_tow_charge ?? null;
    if (parsed === current) return;
    setSavingManualTow(true);
    try {
      const updated = await setManualTowCharge(job.id, parsed);
      // Mutate the caller's job snapshot so the summary rolls the new value.
      Object.assign(job, updated);
      // Snap the visible input to 2dp so "250" renders as "250.00" once saved.
      setManualTowInput(fmt2dp(toGstView(updated.manual_tow_charge)));
      toast.success(parsed == null
        ? "Tow charge cleared"
        : `Tow charge set to $${parsed.toFixed(2)} + ${gstCfg.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tow charge");
    } finally {
      setSavingManualTow(false);
    }
  };

  const commitBillTo = async () => {
    if (!job?.id || !canSetBillTo || savingBillTo) return;
    const name = billToInput.trim();
    if (name === (job.bill_to_name || "")) return;
    setSavingBillTo(true);
    try {
      const res = await setBillTo(job.id, name || null);
      // Mutate the caller's snapshot, as the manual charge does.
      job.bill_to_name = res.bill_to_name || null;
      setBillToInput(res.bill_to_name || "");
      toast.success(res.bill_to_name
        ? `Invoice will be addressed to ${res.contact}`
        : `Back to the name on the job (${res.contact || "none"})`);
    } catch (e) {
      setBillToInput(job.bill_to_name || "");
      toast.error(e?.response?.data?.detail || "Failed to save who this bills to");
    } finally {
      setSavingBillTo(false);
    }
  };

  const commitStorageCharge = async () => {
    if (!job?.id || !canEditManualTow || savingStorageCharge || !storageQuote) return;
    const trimmed = storageChargeInput.trim();
    const typed = trimmed === "" ? null : Number(trimmed);
    if (typed != null && (!Number.isFinite(typed) || typed < 0)) {
      toast.error("Enter a valid amount (or leave blank for days × rate).");
      return;
    }
    const current = storageQuote.charge_adjusted ? storageQuote.total : null;
    if (typed === current && !(typed != null && storageChargeReason.trim()
        && storageChargeReason.trim() !== storageQuote.adjustment_reason)) return;
    if (typed != null && typed !== (storageQuote.total_quoted ?? 0) && !storageChargeReason.trim()) {
      return;  // wait for the reason — the box below is still empty
    }
    setSavingStorageCharge(true);
    try {
      const updated = await setStorageCharge(job.id, typed, storageChargeReason.trim());
      setStorageQuote(updated);
      toast.success(typed == null
        ? "Back to days × rate"
        : `Storage charge set to $${typed.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save the storage charge");
    } finally {
      setSavingStorageCharge(false);
    }
  };

  const commitTowOutCharge = async () => {    if (!outboundJob?.id || !canEditManualTow || savingTowOut) return;
    const trimmed = towOutInput.trim();
    const typed = trimmed === "" ? null : Number(trimmed);
    if (typed != null && (!Number.isFinite(typed) || typed < 0)) {
      toast.error("Enter a valid amount (or leave blank to clear).");
      return;
    }
    const parsed = typed == null ? null : fromGstView(typed);
    if (parsed === (outboundJob.manual_tow_charge ?? null)) return;
    setSavingTowOut(true);
    try {
      const updated = await setManualTowCharge(outboundJob.id, parsed);
      setOutboundJob(updated);
      // Snap the visible input to 2dp so "180" renders as "180.00" once saved.
      setTowOutInput(fmt2dp(toGstView(updated?.manual_tow_charge)));
      toast.success(parsed == null
        ? "Tow-out charge cleared"
        : `Tow-out charge set to $${parsed.toFixed(2)} + ${gstCfg.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tow-out charge");
    } finally {
      setSavingTowOut(false);
    }
  };

  // Build the itemised line items array used by BOTH the print flow and
  // the Email Proforma dialog. Kept as a single source of truth so what
  // the customer sees in their emailed PDF byte-matches what's on the
  // dispatcher's screen (and printout).
  const buildInvoiceLineItems = () => {
    const towTotal =
      (aaQuote && Number(aaQuote.total)) ||
      (job.source === "impound" && job.pricing_breakdown && Number(job.pricing_breakdown.total)) ||
      0;
    const manualTotal = Number(job.manual_tow_charge) || 0;
    const storageTotal = (storageQuote && Number(storageQuote.total)) || 0;
    const towOutTotal = Number(outboundJob?.manual_tow_charge) || 0;
    const items = [];
    if (towTotal > 0) {
      const src = aaQuote?.provider === "first_rescue" ? "First Rescue rate card"
        : aaQuote?.provider === "aa" ? "AA rate card"
        : job.source === "impound" ? "Impound pricing" : "rate card";
      items.push({ label: `Tow in — ${job.address || "pickup"} (${src})`, amount: towTotal });
    }
    if (manualTotal > 0) items.push({ label: "Tow / call-out charge (manual)", amount: manualTotal });
    if (storageTotal > 0) {
      const money = (n) => `$${Number(n || 0).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      items.push({
        label: `Yard storage — ${storageQuote.days_in_storage} day${storageQuote.days_in_storage !== 1 ? "s" : ""} @ ${money(storageQuote.daily_rate)}/day (${storageQuote.rate_category_label || "Default"}) at ${storageQuote.yard_name || "yard"}`,
        amount: storageTotal,
      });
    }
    if (job.outbound_job_id && towOutTotal > 0) {
      items.push({ label: `Tow out — to ${outboundJob?.drop_off_address || "destination"}`, amount: towOutTotal });
    }
    return items;
  };

  const openProformaDialog = async () => {
    let biz = {};
    try { biz = await fetchBusinessInfo(); } catch { /* branding optional */ }
    setProformaCtx({
      lineItems: buildInvoiceLineItems(),
      taxRate: Number(biz.tax_rate ?? 0.15),
      taxLabel: biz.tax_label || "GST",
    });
    setProformaDialogOpen(true);
  };

  // Printable A4 itemised summary (tow in + storage + tow out) for the billing user.
  const printInvoiceSummary = async () => {
    let biz = {};
    try { biz = await fetchBusinessInfo(); } catch { /* letterhead optional */ }
    const money = (n) => `$${Number(n || 0).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const built = buildInvoiceLineItems();
    // Preserve the print layout's slight tweaks: show a placeholder tow-out
    // row even when the amount is 0 (so the print reader sees the pending
    // charge on the linked outbound). buildInvoiceLineItems skips $0 rows
    // for the email PDF because the PDF should never show blank items.
    const towOutTotal = Number(outboundJob?.manual_tow_charge) || 0;
    if (job.outbound_job_id && towOutTotal === 0 && !built.some((it) => it.label.startsWith("Tow out"))) {
      built.push({ label: `Tow out — to ${outboundJob?.drop_off_address || "destination"}`, amount: 0 });
    }
    const items = built.map((it) => [it.label, it.amount]);
    const subtotal = +items.reduce((a, [, v]) => a + v, 0).toFixed(2);
    const taxRate = Number(biz.tax_rate ?? 0.15);
    const tl = biz.tax_label || "GST";
    const gst = +(subtotal * taxRate).toFixed(2);
    const grand = +(subtotal + gst).toFixed(2);
    const stillYarded = !!job.storage_started_at && !job.storage_stopped_at;
    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Tow &amp; Storage Summary — ${job.rego || job.customer_name || ""}</title>
<style>
  @page { size: A4; margin: 18mm 20mm; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 26px; }
  .trading { font-size: 18pt; font-weight: bold; }
  .lh { text-align: right; line-height: 1.4; font-size: 10pt; }
  h1 { font-size: 15pt; letter-spacing: 1px; margin: 0 0 4px; }
  .sub { color: #333; font-size: 10pt; margin-bottom: 18px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 10.5pt; }
  th, td { border: 1px solid #555; padding: 7px 10px; text-align: left; }
  th { background: #f1f5f9; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .right { text-align: right; font-family: 'Courier New', monospace; }
  .tot td { font-weight: bold; }
  .grand td { font-weight: bold; font-size: 12pt; background: #f1f5f9; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .foot { margin-top: 20px; font-size: 9.5pt; color: #333; border-top: 1px solid #999; padding-top: 10px; }
</style></head>
<body>
<div class="top">
  <div>
    <h1>TOW &amp; STORAGE SUMMARY</h1>
    <div class="sub">Itemised charges for invoicing · generated ${new Date().toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}</div>
  </div>
  <div class="lh">
    <div class="trading">${biz.trading_name || ""}</div>
    <div>${biz.trading_as || ""}</div>
    <div>${biz.address_line1 || ""}</div>
    <div>${biz.address_line2 || ""}</div>
    <div>${biz.phone_primary || ""}</div>
  </div>
</div>
<table>
  <tr><th style="width:32%">Registration</th><td class="right">${job.rego || "—"}</td><th style="width:18%">Vehicle</th><td>${job.vehicle || "—"}</td></tr>
  <tr><th>Customer</th><td>${job.customer_name || "—"}</td><th>Job Date</th><td>${job.created_at ? new Date(job.created_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" }) : "—"}</td></tr>
  <tr><th>Pickup</th><td colspan="3">${job.address || "—"}</td></tr>
  <tr><th>Drop-off</th><td colspan="3">${job.drop_off_address || "—"}</td></tr>
</table>
<table>
  <tr><th>Item</th><th class="right" style="width:22%">Amount (ex ${tl})</th></tr>
  ${items.map(([lbl, amt]) => `<tr><td>${lbl}</td><td class="right">${money(amt)}</td></tr>`).join("")}
  <tr class="tot"><td class="right" style="text-align:right">Subtotal (ex ${tl})</td><td class="right">${money(subtotal)}</td></tr>
  <tr class="tot"><td class="right" style="text-align:right">${tl} ${+(taxRate * 100).toFixed(2)}%</td><td class="right">${money(gst)}</td></tr>
  <tr class="grand"><td class="right" style="text-align:right">TOTAL (incl ${tl})</td><td class="right">${money(grand)}</td></tr>
</table>
<div class="foot">
  ${stillYarded
    ? `Vehicle is still in the yard — the storage line keeps accruing daily until it leaves via On-tow out, sign-over to ${biz.salvage_entity_name || "Salvage"}, or DVA release. Figures are as at the print date above.`
    : "Final figures — the storage clock has stopped for this vehicle."}
  ${job.outbound_job_id && !(Number(outboundJob?.manual_tow_charge) > 0) ? " Note: the tow-out charge has not been set yet." : ""}
</div>
</body></html>`;
    printHtmlViaIframe(html, "__invoice_summary_print_frame__");
  };

  if (!job) return null;

  const truck = job.assigned_truck_id ? truckById?.[job.assigned_truck_id] : null;
  const Skip = job.photos_skip_reason ? SKIP_REASON_LABEL[job.photos_skip_reason] : null;
  const sc = STATUS_COLORS[job.status];

  async function handleFilesPicked(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file later
    if (!files.length) return;
    setUploading(true);
    let okCount = 0;
    for (const f of files) {
      try {
        const entry = await uploadJobPhoto(job.id, f);
        setPhotos((cur) => [...cur, entry]);
        okCount += 1;
      } catch (err) {
        toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    if (okCount) toast.success(`Attached ${okCount} photo${okCount === 1 ? "" : "s"}`);
  }

  async function handleDeletePhoto(photo, evt) {
    // Stop click from bubbling to the thumbnail (which would open the zoom view).
    evt?.stopPropagation?.();
    evt?.preventDefault?.();
    const ok = window.confirm(
      `Remove this photo? This permanently deletes the file from disk and can't be undone.\n\nUploaded by ${photo.uploaded_by || "unknown"}.`,
    );
    if (!ok) return;
    const prev = photos;
    setPhotos((cur) => cur.filter((p) => p.id !== photo.id));
    try {
      await deleteJobPhoto(job.id, photo.id);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove photo");
      setPhotos(prev);  // rollback
    }
  }

  function handleEmailInsurer() {
    if (!photos.length) {
      toast.error("No photos to send yet.");
      return;
    }
    setEmailDialogOpen(true);
  }

  // Direct ZIP download — same bundle endpoint the Email Insurer flow uses,
  // but streams the file straight to the browser instead of opening a mail
  // compose window. Added Oct 2026 so a dispatcher chasing archived photos
  // (e.g. HSZ191 / A63104601) can grab the ZIP without needing to send an
  // email first. Falls back to GridFS server-side, so works even on jobs
  // whose on-disk copy was cleared by the retention sweep.
  async function handleDownloadBundle() {
    if (!photos.length) {
      toast.error("No photos on this job.");
      return;
    }
    setDownloadingBundle(true);
    try {
      const { blob, filename } = await downloadJobPhotoBundle(job.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the browser has had a chance to pick up the href.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download photos");
    } finally {
      setDownloadingBundle(false);
    }
  }

  async function handleOpenInPartner() {
    try {
      // Trail marker for the dispatcher timeline (see backend
      // `/partner-opened` — rate-limited, fire-and-forget).
      markJobPartnerOpened(job.id).catch(() => {});
      const res = await fetchJobPartnerLink(job.id);
      if (!res.ok) {
        toast.error("No partner link available for this job");
        return;
      }
      let target;
      if (res.url) {
        target = res.url;
      } else if (res.path) {
        const token = typeof window !== "undefined" ? localStorage.getItem("rescue_token") : null;
        target = `${process.env.REACT_APP_BACKEND_URL}${res.path}${token ? `?t=${token}` : ""}`;
      } else {
        toast.error("No partner link available for this job");
        return;
      }
      window.open(target, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Couldn't open partner app");
    }
  }

  const isAaSource = job.source === "aa_workforms" || job.source === "aa_sms";
  // Deep-login link only for AA Workforms. ServiceNow deep-login was
  // removed per user request — write-back ACLs are not granted yet, and
  // the dispatcher already has the WO number on the card to look up
  // manually in ServiceNow if needed.

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="job-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-black tracking-tight">
            {job.customer_name || "Job"}
            <span className={cn("ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded align-middle", sc?.bg, sc?.text)}>
              {STATUS_LABEL[job.status] || job.status}
            </span>
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2 flex-wrap">
            {job.work_order_number && <span className="text-xs">WO {job.work_order_number}</span>}
            {job.aa_job_id && <span className="text-xs">AA {job.aa_job_id}</span>}
            {job.claim_number && <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200" data-testid="job-detail-claim-number">Ins Claim {job.claim_number}</span>}
            {job.cancelled_on_route && (
              <span
                className={cn(
                  "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded",
                  job.cancel_on_route_charge
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                )}
                data-testid="job-detail-cor-badge"
              >
                Cancelled on Route {job.cancel_on_route_charge ? "— Charge" : "— No charge"}
              </span>
            )}
            {job.source && <span className="text-xs">· {job.source}</span>}
          </SheetDescription>
        </SheetHeader>

        {isAaSource && (
          <Button
            type="button"
            size="sm"
            className="mt-3 w-full bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            onClick={handleOpenInPartner}
            data-testid="job-detail-open-partner-btn"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Open this job in AA Workforms
          </Button>
        )}

        {/* Yard-outbound auto-link banner (Feb 2026).
            Two directions:
              - Child side: this job IS a release from a yard (linked_yard_job_id).
              - Parent side: this yarded job HAS been released (released_via_job_id). */}
        {linkedYardParent && (
          <YardLinkBanner
            side="child"
            other={linkedYardParent}
            currentId={job.id}
            onJump={() => onOpenChange?.(false) || null}
            canUnlink={hasCap(user, "admin", "dispatcher") && !dispatcherReadOnly}
          />
        )}
        {linkedReleaseChild && (
          <YardLinkBanner
            side="parent"
            other={linkedReleaseChild}
            currentId={job.id}
            onJump={() => onOpenChange?.(false) || null}
            canUnlink={false /* unlink flows from the child only */}
          />
        )}

        <div className="space-y-4 mt-4 text-sm">
          <Field icon={Phone} label="Phone" value={job.customer_phone} />
          <Field icon={Car} label="Vehicle" value={`${job.vehicle || ""}${job.rego ? ` · ${job.rego}` : ""}`} />
          {(job.color || job.year_of_manufacture) && (
            <Field icon={Car} label="Colour / Year" value={[job.color, job.year_of_manufacture].filter(Boolean).join(" · ")} />
          )}
          <Field icon={FileText} label="Insurance Claim #" value={job.claim_number} />
          <Field icon={FileText} label="Program" value={job.program} />
          <Field icon={FileText} label="Tow Authority #" value={job.tow_authority_number} />
          <Field icon={Tag} label="Service" value={SERVICE_LABEL[job.service_type] || job.service_type} />
          <Field icon={MapPin} label="Pickup" value={job.address} />
          <Field icon={MapPin} label="Drop-off" value={job.drop_off_address} />
          <Field icon={Truck} label="Truck" value={truck ? `${truck.name}${truck.driver ? " · " + truck.driver : ""}` : "—"} />
          {/* Round-trip km + recalculate/confirm controls. Feb 2026 refit:
              button surfaces even when km is BLANK (email-intake jobs that
              missed km at creation) — clicking POSTs to /km/recalculate,
              same code path AA/SN pollers use. Only shown to staff. */}
          {(() => {
            const canEditKm = ["admin", "dispatcher", "owner"].includes(user?.role);
            const hasKm = job.round_trip_km != null;
            const needsReview = job.km_review_needed && !job.km_confirmed_by;
            const value = hasKm
              ? `${formatDistance(job.round_trip_km * 1000)} (depot ↔ pickup ↔ drop-off ↔ depot)`
              : "— not calculated —";
            return (
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <Field icon={Route} label="Round trip" value={value} />
                  {needsReview && job.km_review_reason && (
                    <div className="ml-6 -mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                      ⚠ {job.km_review_reason}
                    </div>
                  )}
                </div>
                {canEditKm && (!hasKm || needsReview) && (
                  <div className="flex gap-1 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px] font-semibold"
                      disabled={recomputingKm}
                      onClick={async () => {
                        setRecomputingKm(true);
                        try {
                          const r = await recalculateJobKm(job.id);
                          const km = r?.job?.round_trip_km;
                          if (km != null) {
                            toast.success(`Recalculated: ${km.toFixed(1)} km`);
                          } else {
                            toast.warning("Couldn't compute — check pickup and drop-off addresses.");
                          }
                          onOpenChange?.(false);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Recalculate failed");
                        } finally {
                          setRecomputingKm(false);
                        }
                      }}
                      data-testid="job-recalc-km-btn"
                    >
                      {recomputingKm ? <Loader2 className="h-3 w-3 animate-spin" /> : "Recalculate km"}
                    </Button>
                    {needsReview && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        disabled={confirmingKm}
                        onClick={async () => {
                          setConfirmingKm(true);
                          try {
                            await confirmJobKm(job.id);
                            toast.success("Confirmed — km is fine.");
                            onOpenChange?.(false);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Confirm failed");
                          } finally {
                            setConfirmingKm(false);
                          }
                        }}
                        data-testid="job-confirm-km-btn"
                      >
                        {confirmingKm ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          <Field icon={Calendar} label="Created" value={job.created_at ? new Date(job.created_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" }) : "—"} />
          <Field icon={Calendar} label="Completed" value={job.completed_at ? new Date(job.completed_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" }) : "—"} />

          {/* Driver activity trail — full chronological view of what
              the driver did on this job (assigned → accepted → en route
              → opened partner app → on scene → photos → close). Feb 2026. */}
          <JobTimeline job={job} />
          {Skip && (
            <div className={cn("px-3 py-2 rounded text-xs font-semibold flex items-center gap-2", Skip.cls)}>
              <Skip.icon className="h-4 w-4" />
              At-scene photos skipped — reason: {Skip.label}
            </div>
          )}

          {aaQuote && (
            <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30 p-3 space-y-1.5" data-testid="aa-fee-breakdown">
              <div className="flex items-center gap-1.5 text-blue-900 dark:text-blue-200">
                <Receipt className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider">{providerLabel} Fee Breakdown <span className="opacity-60 normal-case font-normal">(ex-GST)</span></span>
              </div>
              {/* Feb 2026 refit — jumpstart no longer treated as a flat-fee
                  special case. The base line changes label based on
                  service_type, then extras (km, FAF, AH, holiday) always
                  render when > 0. Same rate card, unified display. */}
              <>
                <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                  <span>Round trip · depot ↔ pickup ↔ drop-off ↔ depot</span>
                  <span className="font-semibold">{aaQuote.round_trip_km} km</span>
                </div>
                <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                  <span>
                    {aaQuote.service_type === "jumpstart" ? "Jumpstart base" : "Base tow"} (first {aaQuote.breakdown.free_km} km incl.{aaQuote.is_after_hours && aaQuote.provider === "first_rescue" ? ", a/h rate" : ""})
                  </span>
                  <span className="font-semibold">${(aaQuote.service_type === "jumpstart" ? aaQuote.breakdown.jumpstart : aaQuote.breakdown.base_tow).toFixed(2)}</span>
                </div>
                  {aaQuote.breakdown.extra_km > 0 && (
                    <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                      <span>Extra · {aaQuote.breakdown.extra_km} km × ${aaQuote.breakdown.per_km_rate.toFixed(2)}</span>
                      <span className="font-semibold">${aaQuote.breakdown.extra_km_charge.toFixed(2)}</span>
                    </div>
                  )}
                  {/* First Rescue Fuel Adjustment Factor — extra km only */}
                  {aaQuote.breakdown.faf_charge > 0 && (
                    <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                      <span>FAF · {aaQuote.breakdown.extra_km} km × ${aaQuote.breakdown.faf_per_km.toFixed(3)}</span>
                      <span className="font-semibold">${aaQuote.breakdown.faf_charge.toFixed(2)}</span>
                    </div>
                  )}
                  {/* AA puts AH onto a single surcharge; First Rescue swaps base.
                      Both report it on `after_hours_surcharge` line for clarity. */}
                  {aaQuote.breakdown.after_hours_surcharge > 0 && aaQuote.provider !== "first_rescue" && (
                    <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                      <span>
                        {aaQuote.is_public_holiday
                          ? `Public holiday surcharge · ${aaQuote.weekday_name}`
                          : [0, 6].includes(tenantWeekday(aaQuote.at))
                            ? `Weekend surcharge · ${aaQuote.weekday_name}`
                            : "After-hours surcharge · outside 8 am – 5 pm"}
                      </span>
                      <span className="font-semibold">${aaQuote.breakdown.after_hours_surcharge.toFixed(2)}</span>
                    </div>
                  )}
                  {/* First Rescue Public-holiday surcharge — stacks on AH base */}
                  {aaQuote.breakdown.public_holiday_surcharge > 0 && (
                    <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                      <span>Public holiday surcharge · {aaQuote.weekday_name}</span>
                      <span className="font-semibold">${aaQuote.breakdown.public_holiday_surcharge.toFixed(2)}</span>
                    </div>
                  )}
                </>
              <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1.5">
                <span className="font-display font-black text-blue-900 dark:text-blue-100">Total ex-GST</span>
                <span className="font-display text-base font-black text-blue-900 dark:text-blue-100" data-testid="aa-fee-total">${aaQuote.total.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                Calculated from current rate card · round trip (depot → pickup → drop-off → depot) · based on job {job.completed_at ? "completion" : "creation"} time.
              </p>
            </div>
          )}

          {/* Impound fee breakdown — populated by the impound pricing engine
              on create + on any config change. Shows tow fee, impound fee,
              additional-km fee, overstay fee and grand total so the billing user can
              invoice straight from the detail panel. */}
          {job?.source === "impound" && job?.pricing_breakdown?.line_items && (
            <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3 space-y-1.5" data-testid="impound-fee-breakdown">
              <div className="flex items-center gap-1.5 text-emerald-900 dark:text-emerald-200">
                <Receipt className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  Impound Fee Breakdown <span className="opacity-60 normal-case font-normal">(ex-GST)</span>
                </span>
              </div>
              {[
                ["Tow fee (callout)", job.pricing_breakdown.line_items.tow_fee],
                ["Impound fee", job.pricing_breakdown.line_items.impound_fee],
                ["Additional km", job.pricing_breakdown.line_items.additional_km_fee],
                ["Overstay (storage)", job.pricing_breakdown.line_items.overstay_fee],
              ].map(([lbl, amt]) => amt != null && (
                <div key={lbl} className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                  <span>{lbl}</span>
                  <span className="font-semibold">${Number(amt).toFixed(2)}</span>
                </div>
              ))}
              {/* What was actually charged, when the operator settled on a
                  different figure at release. */}
              {job.pricing_breakdown.line_items.adjustment != null && (
                <div className="flex justify-between text-xs text-amber-800 dark:text-amber-300" data-testid="impound-fee-adjustment">
                  <span>
                    Adjustment
                    {job.pricing_breakdown.adjustment_reason
                      ? ` · ${job.pricing_breakdown.adjustment_reason}` : ""}
                  </span>
                  <span className="font-semibold whitespace-nowrap">
                    {Number(job.pricing_breakdown.line_items.adjustment) < 0 ? "−" : "+"}$
                    {Math.abs(Number(job.pricing_breakdown.line_items.adjustment)).toFixed(2)}
                  </span>
                </div>
              )}
              {job.pricing_breakdown.line_items.additional_km_fee != null && job.pricing_breakdown.line_items.additional_km_text && (
                <div className="text-[10px] text-slate-500 dark:text-slate-400 pl-2 italic">
                  {job.pricing_breakdown.line_items.additional_km_text}
                </div>
              )}
              <div className="flex justify-between border-t border-emerald-300 dark:border-emerald-700 pt-1.5 mt-1.5 text-sm">
                <span className="font-bold text-emerald-900 dark:text-emerald-100">TOTAL</span>
                <span className="font-display text-base font-black text-emerald-900 dark:text-emerald-100" data-testid="impound-fee-total">
                  ${Number(job.pricing_breakdown.total || 0).toFixed(2)}
                </span>
              </div>
              {job.pricing_breakdown.total_quoted != null && (
                <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                  Quoted ${Number(job.pricing_breakdown.total_quoted).toFixed(2)} — charge adjusted at release.
                </p>
              )}
            </div>
          )}

          {/* Geofence-suggested storage — appears when drop-off was inside a
              yard radius BUT the dispatcher hasn't confirmed yet. Storage
              clock is NOT running until they click Confirm. */}
          {job?.storage_suggested_yard && !job?.storage_started_at && !job?.storage_disabled && (
            <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2" data-testid="storage-suggestion">
              <div className="flex items-center gap-1.5 text-amber-900 dark:text-amber-200">
                <Warehouse className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider">Storage suggestion</span>
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-200">
                Drop-off GPS lands inside the <b className="capitalize">{job.storage_suggested_yard.replace("_", " ")}</b> yard geofence.
                Was this vehicle <b>stored at our yard</b>, or was it a delivery nearby?
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white text-xs h-7 px-2"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/jobs/${job.id}/storage/confirm`);
                      setStorageQuote(r.data);
                      toast.success("Storage clock started");
                      job.storage_suggested_yard = null;
                      job.storage_started_at = r.data.started_at;
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="storage-confirm"
                >✓ Yes, start storage</Button>
                <Button
                  size="sm" variant="outline"
                  className="text-xs h-7 px-2"
                  onClick={async () => {
                    try {
                      await api.post(`/jobs/${job.id}/storage/decline`);
                      toast.success("Marked as delivery — no storage");
                      job.storage_suggested_yard = null;
                      job.storage_disabled = true;
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="storage-decline"
                >✗ No, delivery only</Button>
              </div>
            </div>
          )}

          {storageQuote && storageQuote.started_at && (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30 p-3 space-y-1.5" data-testid="storage-fee-breakdown">
              <div className="flex items-center gap-1.5 text-amber-900 dark:text-amber-200">
                <Warehouse className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider">Yard Storage <span className="opacity-60 normal-case font-normal">(ex-GST)</span></span>
              </div>
              <div className="flex justify-between items-center text-xs text-slate-700 dark:text-slate-200">
                <span>Stored at</span>
                <select
                  value={storageQuote.yard_slug || ""}
                  onChange={(e) => handleChangeYard(e.target.value)}
                  disabled={savingYard || !storageQuote.yards?.length}
                  className="rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold"
                  data-testid="storage-yard-select"
                >
                  {storageQuote.yards?.map((y) => (
                    <option key={y.slug} value={y.slug}>{y.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between items-center text-xs text-slate-700 dark:text-slate-200">
                <span>Rate category</span>
                <span className="font-semibold text-right" data-testid="storage-rate-category-label">
                  {storageQuote.rate_category_label || "Default"}
                  {storageQuote.rate_source === "default" && (
                    <span className="ml-1 text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-200/80 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200" title={`No party matched — using the impound default rate. Set the right category on the ${salvageShort()} Yards page.`}>
                      confirm
                    </span>
                  )}
                </span>
              </div>
              <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                <span>Days in storage · ${storageQuote.daily_rate.toFixed(2)}/day</span>
                <span className="font-semibold">{storageQuote.days_in_storage} day{storageQuote.days_in_storage !== 1 ? "s" : ""}</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 dark:border-amber-800 pt-1.5">
                <span className="font-display font-black text-amber-900 dark:text-amber-100">Storage total</span>
                <span className="font-display text-base font-black text-amber-900 dark:text-amber-100" data-testid="storage-fee-total">${storageQuote.total.toFixed(2)}</span>
              </div>
              {/* The days are arithmetic; the charge is a decision. The
                  weekend a car sat waiting for Monday often isn't charged,
                  and before this the difference became a small odd invoice. */}
              {canEditManualTow ? (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-700 dark:text-slate-200">Charge instead</span>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                      <input
                        type="number" step="0.01" min="0"
                        className="w-full rounded border border-amber-300 dark:border-amber-700 dark:bg-slate-800 pl-5 pr-2 py-1 text-xs font-mono text-right"
                        placeholder={storageQuote.total_quoted?.toFixed(2) ?? ""}
                        value={storageChargeInput}
                        onChange={(e) => setStorageChargeInput(e.target.value)}
                        onBlur={commitStorageCharge}
                        disabled={savingStorageCharge}
                        data-testid="storage-charge-override"
                      />
                    </div>
                  </div>
                  {(storageChargeInput.trim() !== ""
                    && Number(storageChargeInput) !== storageQuote.total_quoted) && (
                    <input
                      className="w-full rounded border border-amber-300 dark:border-amber-700 dark:bg-slate-800 px-2 py-1 text-xs"
                      placeholder="Why? e.g. weekend not charged"
                      value={storageChargeReason}
                      onChange={(e) => setStorageChargeReason(e.target.value)}
                      onBlur={commitStorageCharge}
                      data-testid="storage-charge-reason"
                    />
                  )}
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                    {storageQuote.charge_adjusted
                      ? `Charging $${storageQuote.total.toFixed(2)} instead of $${(storageQuote.total_quoted ?? 0).toFixed(2)} — ${storageQuote.adjustment_reason || "no reason given"}. Clear the box to go back to days × rate.`
                      : "Leave blank to charge days × rate."}
                  </p>
                </div>
              ) : storageQuote.charge_adjusted && (
                <p className="text-[10px] text-amber-800 dark:text-amber-300 italic" data-testid="storage-charge-note">
                  Adjusted from ${(storageQuote.total_quoted ?? 0).toFixed(2)} — {storageQuote.adjustment_reason || "no reason given"}
                </p>
              )}
              <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                Accrues from the first NZ midnight after drop-off. Yard auto-detected from drop-off GPS — dispatch can override.
              </p>
            </div>
          )}

          {/* Combined "Invoice Summary" — shows the grand total the billing user should
              actually raise for this job. Only renders on completed /
              cancelled jobs (nothing to invoice before then) and only when at
              least one dollar figure is known — OR the user can enter a
              manual tow charge, in which case we always render so they have
              somewhere to type it. Sums provider tow quote (AA/FR or impound
              breakdown) + manual tow charge + yard storage total. */}
          {(() => {
            const isBillable = job.status === "completed" || job.status === "cancelled";
            if (!isBillable) return null;
            const towTotal =
              (aaQuote && Number(aaQuote.total)) ||
              (job.source === "impound" && job.pricing_breakdown && Number(job.pricing_breakdown.total)) ||
              0;
            const manualTotal = Number(job.manual_tow_charge) || 0;
            const storageTotal = (storageQuote && Number(storageQuote.total)) || 0;
            const towOutTotal = Number(outboundJob?.manual_tow_charge) || 0;
            if (towTotal === 0 && storageTotal === 0 && manualTotal === 0 && towOutTotal === 0 && !canEditManualTow) return null;
            const grand = towTotal + manualTotal + storageTotal + towOutTotal;
            const stillYarded = !!job.storage_started_at && !job.storage_stopped_at;
            return (
              <div
                className={cn(
                  "rounded-md border p-3 space-y-1.5",
                  stillYarded
                    ? "border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40"
                    : "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30",
                )}
                data-testid="invoice-summary"
              >
                <div className={cn(
                  "flex flex-wrap items-center gap-x-1.5 gap-y-1",
                  stillYarded ? "text-slate-700 dark:text-slate-300" : "text-emerald-900 dark:text-emerald-200",
                )}>
                  <Receipt className="h-4 w-4" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">
                    Invoice Summary
                  </span>
                  <div
                    className={cn(
                      "inline-flex shrink-0 overflow-hidden rounded-full border text-[9px] font-bold uppercase tracking-wider",
                      stillYarded
                        ? "border-slate-300 dark:border-slate-600"
                        : "border-emerald-300 dark:border-emerald-800",
                    )}
                    role="group"
                    aria-label="Show totals excluding or including GST"
                  >
                    {[
                      { incl: false, label: `ex-${gstCfg.label}` },
                      { incl: true, label: `incl-${gstCfg.label}` },
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => setShowInclGst(opt.incl)}
                        aria-pressed={showInclGst === opt.incl}
                        className={cn(
                          "px-2 py-0.5 whitespace-nowrap transition-colors duration-150",
                          showInclGst === opt.incl
                            ? stillYarded
                              ? "bg-slate-600 text-white"
                              : "bg-emerald-700 text-white dark:bg-emerald-600"
                            : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100",
                        )}
                        data-testid={`invoice-summary-gst-${opt.incl ? "incl" : "ex"}-btn`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={printInvoiceSummary}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300"
                    data-testid="invoice-summary-print-btn"
                    title="Print itemised tow + storage + tow-out summary"
                  >
                    <Printer className="h-3.5 w-3.5" /> Print
                  </button>
                  {(hasCap(user, "admin", "dispatcher")) && !dispatcherReadOnly && (
                    <button
                      type="button"
                      onClick={openProformaDialog}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300"
                      data-testid="invoice-summary-proforma-btn"
                      title="Email a proforma invoice to the customer so they can pay by online banking"
                    >
                      <Mail className="h-3.5 w-3.5" /> Email Proforma
                    </button>
                  )}
                </div>
                {job.round_trip_km != null && (
                  <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                    <span>Round trip</span>
                    <span className="font-semibold">{formatDistance(job.round_trip_km * 1000)}</span>
                  </div>
                )}
                {towTotal > 0 && (
                  <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                      <span data-testid="invoice-summary-tow-label">
                        Tow &amp; kms{aaQuote?.provider === "first_rescue"
                          ? " (First Rescue)"
                          : aaQuote?.provider === "aa"
                          ? " (AA)"
                          : job.source === "impound"
                          ? " (Impound)"
                          : ""}
                    </span>
                    <span className="font-semibold" data-testid="invoice-summary-tow">${toGstView(towTotal).toFixed(2)}</span>
                  </div>
                )}
                {/* Manual customer tow charge — direct-customer tows have no
                    rate card, so billing/dispatch key the agreed price here.
                    Also serves as a top-up for provider jobs (waiting time,
                    chain hire, after-hours). Left-aligned label mimics the
                    other summary rows; the right side is either the saved
                    figure (read-only) or an inline input. */}
                <div className="flex justify-between items-center text-xs text-slate-700 dark:text-slate-200 gap-2">
                  <span className="whitespace-nowrap">
                    Tow charge
                    {showInclGst && (
                      <span className="ml-1 text-[10px] font-normal opacity-60" data-testid="invoice-summary-manual-gst-hint">
                        incl {gstCfg.label}
                      </span>
                    )}
                  </span>
                  {canEditManualTow ? (
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500 dark:text-slate-400 font-mono">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={manualTowInput}
                        onChange={(e) => setManualTowInput(e.target.value.replace(/[^0-9.]/g, ""))}
                        onBlur={commitManualTowCharge}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        disabled={savingManualTow}
                        placeholder="0.00"
                        className="w-24 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-0.5 text-right text-xs font-semibold tabular-nums disabled:opacity-60"
                        data-testid="invoice-summary-manual-input"
                      />
                      {savingManualTow && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                    </div>
                  ) : (
                    <span className="font-semibold tabular-nums" data-testid="invoice-summary-manual-readonly">
                      {manualTotal > 0 ? `$${toGstView(manualTotal).toFixed(2)}` : "—"}
                    </span>
                  )}
                </div>
                {storageTotal > 0 && (
                  <div className="flex justify-between text-xs text-slate-700 dark:text-slate-200">
                    <span>Yard storage · {storageQuote.days_in_storage} day{storageQuote.days_in_storage !== 1 ? "s" : ""}</span>
                    <span className="font-semibold" data-testid="invoice-summary-storage">${toGstView(storageTotal).toFixed(2)}</span>
                  </div>
                )}
                {/* Tow out — the linked outbound job created via On-tow out.
                    Charged separately; editable inline like the manual charge
                    so the billing user can price the whole in/store/out cycle here. */}
                {job.outbound_job_id && (
                  <div className="flex justify-between items-center text-xs text-slate-700 dark:text-slate-200 gap-2">
                    <span className="whitespace-nowrap">
                      Tow out{outboundJob?.status ? ` · ${STATUS_LABEL[outboundJob.status] || outboundJob.status}` : ""}
                    </span>
                    {canEditManualTow && outboundJob ? (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500 dark:text-slate-400 font-mono">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={towOutInput}
                          onChange={(e) => setTowOutInput(e.target.value.replace(/[^0-9.]/g, ""))}
                          onBlur={commitTowOutCharge}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          disabled={savingTowOut}
                          placeholder="0.00"
                          className="w-24 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-0.5 text-right text-xs font-semibold tabular-nums disabled:opacity-60"
                          data-testid="invoice-summary-towout-input"
                        />
                        {savingTowOut && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                      </div>
                    ) : (
                      <span className="font-semibold tabular-nums" data-testid="invoice-summary-towout-readonly">
                        {towOutTotal > 0 ? `$${toGstView(towOutTotal).toFixed(2)}` : "—"}
                      </span>
                    )}
                  </div>
                )}
                <div className={cn(
                  "flex justify-between border-t pt-1.5 mt-1",
                  stillYarded ? "border-slate-300 dark:border-slate-600" : "border-emerald-300 dark:border-emerald-800",
                )}>
                  <span className={cn(
                    "font-display font-black",
                    stillYarded ? "text-slate-800 dark:text-slate-100" : "text-emerald-900 dark:text-emerald-100",
                  )}>
                    Total to invoice
                    <span className="ml-1 text-[10px] font-normal opacity-60">
                      {showInclGst ? `incl ${gstCfg.label}` : `ex ${gstCfg.label}`}
                    </span>
                  </span>
                  <span className={cn(
                    "font-display text-lg font-black",
                    stillYarded ? "text-slate-800 dark:text-slate-100" : "text-emerald-900 dark:text-emerald-100",
                  )} data-testid="invoice-summary-total">
                    ${toGstView(grand).toFixed(2)}
                  </span>
                </div>

                {/* Who the invoice is addressed to in Xero. The dispatcher
                    taking the job can't know the account name, and a name
                    that doesn't match creates a second Xero contact rather
                    than failing — so it's the office's call, here. */}
                {canSetBillTo && (
                  <div className="pt-1.5 mt-1 border-t border-slate-200 dark:border-slate-700 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-700 dark:text-slate-200 whitespace-nowrap">Bills to</span>
                      <input
                        list={`payers-${job.id}`}
                        value={billToInput}
                        onChange={(e) => setBillToInput(e.target.value)}
                        onBlur={commitBillTo}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        disabled={savingBillTo || !!job.invoice_number}
                        placeholder={job.customer_name || "Name as it is in Xero"}
                        className="flex-1 min-w-0 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-2 py-0.5 text-xs disabled:opacity-60"
                        data-testid="invoice-summary-bill-to"
                      />
                      {savingBillTo && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                    </div>
                    <datalist id={`payers-${job.id}`}>
                      {payers.map((p) => <option key={p} value={p} />)}
                    </datalist>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                      {job.invoice_number
                        ? `Invoice ${job.invoice_number} has already gone out.`
                        : "Leave blank to use the name on the job. A name Xero doesn't hold creates a new contact."}
                    </p>
                  </div>
                )}
                {stillYarded ? (
                  <p className="text-[10px] text-slate-600 dark:text-slate-400 italic flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Vehicle still in yard — storage total will keep growing until it&apos;s released via On-tow out, Sign Over, or Released DVA.
                  </p>
                ) : (
                  <p className="text-[10px] text-emerald-800/80 dark:text-emerald-300/80 italic">
                    Final totals. Flip the Invoiced pill on the History row once you&apos;ve raised the invoice.
                  </p>
                )}
              </div>
            );
          })()}

          {job.notes && (
            <div>
              <Label icon={FileText} text="Notes" />
              <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded p-2 whitespace-pre-wrap">
                {job.notes}
              </div>
            </div>
          )}

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label icon={Camera} text={`Damage Photos (${photos.length})`} />
              {canReadPhotoActions && (
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFilesPicked}
                    data-testid="job-photos-attach-input"
                  />
                  {canAttach && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 px-2.5 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="job-photos-attach-btn"
                    >
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {uploading ? "Uploading…" : "Attach"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-2.5 text-xs"
                    onClick={handleDownloadBundle}
                    disabled={photos.length === 0 || downloadingBundle}
                    data-testid="job-photos-download-btn"
                    title="Download all photos as a ZIP"
                  >
                    {downloadingBundle ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    {downloadingBundle ? "Zipping…" : "Download"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-2.5 text-xs"
                    onClick={handleEmailInsurer}
                    disabled={photos.length === 0}
                    data-testid="job-photos-email-btn"
                  >
                    <Mail className="h-3 w-3" />
                    Email Insurer
                  </Button>
                </div>
              )}
            </div>
            {photos.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-2">
                No photos attached to this job.{canAttach ? " Use Attach to add some." : ""}
              </div>
            ) : (
              <>
                {/* `photos_purged_at` means the on-disk copy was cleared by the
                    60-day retention sweep. GridFS still typically has the bytes
                    though (the pre-Oct-2026 purge routine only wiped disk and
                    stamped the flag), so we still render thumbnails + Email
                    Insurer rather than gating the panel. If a photo truly is
                    missing from both stores the <img> will show broken, which
                    is the honest signal. Show a slim retention note above the
                    grid so a dispatcher knows why the panel looks a bit less
                    polished than for a fresh job. */}
                {job.photos_purged_at && (
                  <div
                    className="text-[11px] italic text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 rounded px-2.5 py-1.5 border border-dashed border-slate-300 dark:border-slate-700 mb-2"
                    data-testid="job-photos-retention-note"
                  >
                    Retention sweep ran {new Date(job.photos_purged_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz() })}.
                    If a thumbnail fails to load, that photo is no longer recoverable.
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2" data-testid="job-photos-grid">
                  {photos.map((p) => (
                    <div key={p.id} className="relative group">
                      <button
                        onClick={() => setZoomed(p)}
                        className="aspect-square w-full overflow-hidden rounded border border-slate-200 dark:border-slate-700 hover:border-blue-400"
                        data-testid={`job-photo-${p.id}`}
                      >
                        <img
                          src={photoSrc(p)}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                      {canAttach && (
                        <button
                          type="button"
                          onClick={(e) => handleDeletePhoto(p, e)}
                          className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-rose-300"
                          aria-label="Remove this photo"
                          title={`Remove photo (uploaded by ${p.uploaded_by || "unknown"})`}
                          data-testid={`job-photo-remove-${p.id}`}
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {(hasCap(user, "admin", "dispatcher")) && <JobHistoryPanel jobId={job.id} />}
        </div>

        {/* Photo zoom overlay */}
        {zoomed && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setZoomed(null)}
            data-testid="photo-zoom-overlay"
          >
            <img
              src={photoSrc(zoomed)}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}

        {/* Email Insurer dialog (Resend-backed send) */}
        <EmailInsurerDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          job={job}
        />

        {/* Email Proforma dialog — bank-transfer invoice to customer */}
        <EmailProformaDialog
          open={proformaDialogOpen}
          onOpenChange={setProformaDialogOpen}
          job={job}
          lineItems={proformaCtx?.lineItems || []}
          grandTotal={(proformaCtx?.lineItems || []).reduce((a, li) => a + Number(li.amount || 0), 0)}
          taxRate={proformaCtx?.taxRate ?? 0.15}
          taxLabel={proformaCtx?.taxLabel || "GST"}
        />
      </SheetContent>
    </Sheet>
  );
}

function Field({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 break-words">{value}</div>
      </div>
    </div>
  );
}

function Label({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
      <Icon className="h-3 w-3" /> {text}
    </div>
  );
}

// -----------------------------------------------------------------------------
// YardLinkBanner (Feb 2026) — cross-link display for the yard-outbound auto-link
// -----------------------------------------------------------------------------
// Renders a small emerald ribbon at the top of the detail sheet showing:
//   • Child side ("this job releases XYZ from Parkers Rd") — with admin unlink.
//   • Parent side ("this yarded vehicle is being released by <inbound rego>").
// The counter-party job's id is exposed as a `data-testid` so playwright can
// click-through without duplicating the whole open/close dance.
function YardLinkBanner({ side, other, currentId, onJump, canUnlink }) {
  const [unlinking, setUnlinking] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const yard = (other?.storage_yard || other?.yard_slug || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const doUnlink = async () => {
    if (!confirmingUnlink) { setConfirmingUnlink(true); return; }
    setUnlinking(true);
    try {
      await unlinkYard(currentId);
      toast.success("Yard link removed. Storage clock resumed if it had been stopped.");
      window.dispatchEvent(new Event("jobs:refresh"));
      onJump?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    } finally {
      setUnlinking(false);
    }
  };
  return (
    <div
      className="mt-3 rounded-md border-2 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3"
      data-testid={`job-yard-link-banner-${side}`}
    >
      <div className="flex items-start gap-2">
        {side === "child" ? (
          <Undo2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" strokeWidth={2.25} />
        ) : (
          <Link2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" strokeWidth={2.25} />
        )}
        <div className="text-xs text-emerald-900 dark:text-emerald-100 flex-1">
          <div className="font-bold">
            {side === "child"
              ? <>Release from {yard || "yard"}</>
              : <>Being released by inbound job</>}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
            {other.rego && <span className="font-mono font-bold">{other.rego}</span>}
            {other.customer_name && <span>· {other.customer_name}</span>}
            {other.status && <span className="text-emerald-700 dark:text-emerald-300">· {STATUS_LABEL[other.status] || other.status}</span>}
          </div>
          <div className="mt-1 text-[10px] text-emerald-800/80 dark:text-emerald-200/80">
            {side === "child"
              ? "Storage clock stops the moment this truck reaches on-site at the yard."
              : "The linked outbound job will stop the storage clock on its first on-site."}
          </div>
        </div>
      </div>
      {canUnlink && side === "child" && (
        <div className="mt-2 flex justify-end gap-1.5">
          {confirmingUnlink && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-xs h-7 px-2"
              onClick={() => setConfirmingUnlink(false)}
              disabled={unlinking}
              data-testid="yard-unlink-cancel-btn"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className={cn(
              "text-xs h-7 px-2",
              confirmingUnlink
                ? "border border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
                : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white",
            )}
            onClick={doUnlink}
            disabled={unlinking}
            data-testid="yard-unlink-btn"
          >
            {unlinking ? "Unlinking..." : confirmingUnlink ? "Confirm unlink" : "Unlink"}
          </Button>
        </div>
      )}
    </div>
  );
}
